import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import * as R from 'runtypes';
import { Static } from 'runtypes';

import { URLS } from 'commons/app.constant';
import { CandlePeriodicity } from './chartiq/chart_component/candle.periodicity';
import { Theme } from './chartiq/theme';
import { Preferences } from 'entities/preferences';
import { LedgerEntryType, TransactionEntry } from 'entities/transaction.entry';
import {
  OrderSide,
  OrderStatus,
  Instrument,
  MarketService,
  LedgerType,
  FeeTypes,
  SuccessOnlyResponse,
  defined,
  HttpOptions,
  TwoFactorData,
} from 'core';
import { buildSingleOrder, fromOrders, Order, OrderResponse } from 'entities/order';
import { SendAddress, SendAddressType } from 'entities/send.address';

export class ChartComparison {
  constructor(readonly market: string, readonly colourCode: string) {}
}

enum OrderSideSearchOption {
  Combined = 'COMBINED',
}

// This is how we pretend to "extend" enums
export type OrderSideQuery = OrderSide | OrderSideSearchOption;
export const OrderSideQuery = { ...OrderSide, ...OrderSideSearchOption };

const ApiZoomMeDetails = R.Record({
  firstName: R.String.nullable(),
  lastName: R.String.nullable(),
  kycApproved: R.Boolean,
  receiveUsd: R.Boolean,
}).asReadonly();
export type ZoomMeDetails = Static<typeof ApiZoomMeDetails>;

export type LedgerEntry = {
  id: { ledgerId: number; entryId: number };
  availableBalance: string;
  entryAmt: string;
  entryComment: string;
  userComment: string;
  entryRef: string;
  entryTs: string; // '2021-04-15T22:25:10.462805Z'
  ledgerEntryTypeEn: string;
  ledgerEntrySubTypeEn: string;
  displayTypeId: string;
  reservedOpeningAmt: number;
  totalBalance: string;
  hash?: string;
};

export type SecondFactorConfig = { trade2FARequired: boolean; withdraw2FARequired: boolean; zoomMe2FARequired: boolean };

export type BaseSendDetails = {
  amount: number;
  feePaymentMethodEn: FeeTypes;

  zoommeDestination?: string | null;
  currency?: string;
  note?: string;
  alternativeStableCoinCd: string | null;
};

export type SendDetails = BaseSendDetails & {
  destination: SendAddress;
};

export type ChilledSendDetails = BaseSendDetails & {
  otp?: number;
  network: string;
};

const ApiPrimeLevel = R.Record({
  level: R.String,
  qualifyingZoomAmt: R.Number,
  tradingFeeDiscountPct: R.Number,
}).asReadonly();

const ApiPrimeMetadata = R.Record({
  levels: R.Array(ApiPrimeLevel),
}).asReadonly();
export type ApiPrimeMetadata = Static<typeof ApiPrimeMetadata>;

const KycLimits = R.Record({
  maximumEffectiveZoomBalanceAmt: R.Number.nullable(),
}).asReadonly();
export type KycLimits = Static<typeof KycLimits>;

@Injectable({
  providedIn: 'root',
})
export class CommonsService {
  private userPreferenceCache: Observable<Preferences | null>;

  constructor(private readonly http: HttpClient, private readonly marketService: MarketService) {}

  acceptTermsAndConditions(): Observable<unknown> {
    return this.http.post(URLS.acceptTermsAndConditions, null);
  }

  getLedgerEntryTypes(): Observable<LedgerEntryType[]> {
    return this.http.get<LedgerEntryType[]>(URLS.ledgerEntryTypesURl);
  }

  getZoomCommissionList(): Observable<ApiPrimeMetadata> {
    return this.http.get(URLS.primeMetadataUrl).pipe(map(ApiPrimeMetadata.check));
  }

  getKycLimits(): Observable<KycLimits> {
    return this.http.get(URLS.kycLimitsUrl).pipe(map(KycLimits.check));
  }

  // TODO move to MarketDataService or a new service above that
  getOrders(
    lastOrderNumber: string | null | undefined,
    orderSide: OrderSideQuery,
    statuses: Array<OrderStatus>,
    startDate?: string,
    endDate?: string,
    pageSize?: number
  ): Observable<Array<Order>> {
    const request = {
      bookmarkOrderId: lastOrderNumber,
      size: pageSize ? pageSize : 50,
      ascending: false,
      orderSide: orderSide === OrderSideQuery.Combined ? undefined : orderSide,
      orderStatuses: statuses,
      start: startDate,
      end: endDate,
    };

    return combineLatest([
      this.http.post<Array<OrderResponse>>(URLS.orderHistoryUrl, request),
      this.marketService.instruments.pipe(map((instruments) => new Map(instruments.map((instrument) => [instrument.id, instrument])))),
    ]).pipe(map(([orders, instrumentsById]) => fromOrders(orders, instrumentsById)));
  }

  getOrder(orderId: string, instrument: Instrument): Observable<Order> {
    return this.http.get<OrderResponse>(URLS.orderUrl + orderId).pipe(map((order) => buildSingleOrder(order, instrument)));
  }

  wireWithdrawal(withdrawal: any, twoFAData: TwoFactorData | null): Observable<void> {
    return this.http.post<void>(URLS.wirewithdrawalUrl, withdrawal, HttpOptions.builder().withTwoFactor(twoFAData).build());
  }

  sendAPI(sendData: SendDetails, twoFA?: TwoFactorData): Observable<void> {
    let destinationAddress: string | null = null;
    if (sendData.destination.type !== SendAddressType.CHILLED_VAULT) {
      destinationAddress = sendData.destination.submitAddress;
    }

    const postData = {
      currencyCd: sendData.currency,
      alternativeStableCoinCd: sendData.alternativeStableCoinCd,
      amount: sendData.amount,
      network: sendData.destination.network,
      destinationAddress,
      toChilledWallet: sendData.destination.type === SendAddressType.CHILLED_VAULT,
      feePaymentMethod: sendData.feePaymentMethodEn,
      note: sendData.note,
    };

    return this.http.post<void>(URLS.sendwallet, postData, HttpOptions.builder().withTwoFactor(twoFA).build());
  }

  sendToChilled(sendData: ChilledSendDetails, twoFA: TwoFactorData | undefined): Observable<void> {
    const postData = {
      currencyCd: sendData.currency,
      amount: sendData.amount,
      network: sendData.network,
      destinationAddress: null,
      toChilledWallet: true,
      feePaymentMethod: sendData.feePaymentMethodEn,
      note: sendData.note,
    };

    return this.http.post<void>(URLS.sendwallet, postData, HttpOptions.builder().withTwoFactor(twoFA).build());
  }

  getTransactions(
    firstPage: boolean,
    lastTs: string | null,
    currency: string,
    ledgerType: LedgerType,
    ledgerEntryTypes: Array<string>,
    startDate?: string,
    endDate?: string,
    pageSize?: number,
    includeHashInResults?: boolean
  ): Observable<Array<LedgerEntry>> {
    const req = {
      currencyCd: currency,
      timeExclusion: firstPage ? 'NONE' : 'END',
      ledgerTypeEn: ledgerType,
      start: startDate,
      end: lastTs ?? endDate,
      ledgerEntryTypes,
      ascending: false,
      size: pageSize ? pageSize : 50,
      includeHashInResults,
    };
    return this.http.post<Array<LedgerEntry>>(URLS.transactionHistoryUrl, req);
  }

  getTransactionHash(entry: TransactionEntry): Observable<string | undefined> {
    return this.http.get<string | undefined>(URLS.transactionHashUrl(entry.ledgerEntryTypeEn, entry.entryRef!));
  }

  public cancelOrder(orderId: string, symbolCd: string): Observable<any> {
    return this.http.post(URLS.cancelOrder, { orderId, symbol: symbolCd });
  }

  /** function to make a get call with an observer to store the preference data in cache for further use */
  getPersonalizedSettingsApi(): Observable<Preferences | null> {
    if (!this.userPreferenceCache) {
      this.userPreferenceCache = this.http.get(URLS.personalizedSettings + 'false/personalized_settings').pipe(
        map((preferenceData: any) => {
          if (preferenceData?.responseObject) {
            return new Preferences(
              preferenceData.responseObject.termCurrency,
              preferenceData.responseObject.selectedMarket,
              preferenceData.responseObject.showZeroBalances,
              preferenceData.responseObject.chartSettings
            );
          }
          return null;
        }),
        shareReplay({ refCount: true, bufferSize: 1 })
      );
    }
    return this.userPreferenceCache;
  }

  /** Function to make a put call to update the data and preferences */
  setPersonalizedSettingsApi(preference: Preferences): Observable<Preferences> {
    const payload = {
      termCurrency: preference.termCurrency,
      selectedMarket: preference.selectedMarket,
      showZeroBalances: preference.showZeroBalances,
    };
    const updatedPreferences = this.http
      .put<SuccessOnlyResponse<Preferences>>(URLS.personalizedSettings + 'false/personalized_settings', payload)
      .pipe(
        map(
          (preferenceData) =>
            new Preferences(
              preferenceData.responseObject.termCurrency,
              preferenceData.responseObject.selectedMarket,
              preferenceData.responseObject.showZeroBalances,
              preferenceData.responseObject.chartSettings
            )
        ),
        shareReplay({ refCount: true, bufferSize: 1 })
      );
    this.userPreferenceCache = updatedPreferences;
    return updatedPreferences;
  }

  setChartHistorySettingsApi(
    periodicity: CandlePeriodicity,
    candleType: string | null,
    displayedChart: string,
    crosshairs: boolean,
    selectedTimeZone: string,
    comparisons: ChartComparison[],
    studies: string[],
    themes: Theme[]
  ): Observable<any> {
    const request = {
      candleSize: periodicity != null ? periodicity.backendId : null,
      candleStyle: candleType,
      displayedChart,
      timeZoneSelected: selectedTimeZone,
      comparisons,
      studies,
      crosshairs,
      themes,
    };

    this.userPreferenceCache = combineLatest([
      this.userPreferenceCache.pipe(filter(defined)),
      this.http.put<any>(URLS.personalizedSettings + 'false/personalized_settings/charts', request),
    ]).pipe(
      map(([cache, result]) => {
        cache.chartSettings = result.responseObject;
        return cache;
      }),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    return this.userPreferenceCache.pipe(map((cache) => cache!.chartSettings));
  }

  getAdvanced2FactorConfig(): Observable<SuccessOnlyResponse<SecondFactorConfig>> {
    return this.http.get<SuccessOnlyResponse<SecondFactorConfig>>(URLS.getAdvanceConfig);
  }

  getZoommeHandleDetails(zoomMe: string): Observable<ZoomMeDetails> {
    const zoommeHandleNameUrl = URLS.zoommeHandleUrl.replace('{handle}', zoomMe);
    return this.http.get(zoommeHandleNameUrl).pipe(map(ApiZoomMeDetails.check));
  }

  checkIsInternalAddress(sendAddress: SendAddress, currencySymbol: string): any {
    return this.http.get(
      URLS.checkAddressType.replace('{ccy}', currencySymbol).replace('{address}', encodeURIComponent(sendAddress.submitAddress))
    );
  }

  amountPositive(amount?: string | number): boolean {
    if (amount || amount === 0) {
      const numericAmount: number = typeof amount === 'number' ? amount : parseFloat(amount);
      if (!isNaN(numericAmount) && isFinite(numericAmount)) {
        return numericAmount > 0;
      }
    }

    return true;
  }

  // Pass-though to reduce caller's dependencies
  getInstrument(symbolCd: string): Promise<Instrument> {
    return this.marketService.getInstrument(symbolCd);
  }
}
